import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { PaymentMethod } from '@stripe/stripe-js';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import {
  ANIMATION_DURATION,
  HEADER_Z_INDEX,
  IconButton,
  Layout,
  Link,
  LoadingScreen,
  PillButton,
  StickyContainer,
  Text
} from 'ds';
import { APP_PAGE_PADDING_X_MOBILE } from 'ds/PageLayout/constants';
import PaymentMethodCard from 'ds/PaymentMethodCard';
import StripePaymentMethodModal from 'ds/StripePaymentMethodModal';
import { listPaymentMethods } from 'ds/StripePaymentMethodModal/requests';
import UpdateStripePaymentMethodModal from 'ds/UpdateStripePaymentMethodModal';
import { formatUsd } from 'helpers';
import { PANTRY_SUBSCRIPTION_PATH } from 'routes/paths';
import { formatDate } from 'shared';
import { ConsumablesProductSubscription } from 'shared/types';
import { actions } from 'store/Consumables';
import { selectDeliveryFees, selectProductSubscriptions } from 'store/Consumables/selectors';
import { actions as tenancyActions } from 'store/Tenancy';
import { selectSalesTaxRate, selectServicesPaymentMethod } from 'store/Tenancy/selectors';
import { selectUIState } from 'store/UI/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';

import ConsumablesOnboardingRestockingBanner from './ConsumablesOnboardingRestockingBanner';
import ConsumablesOnboardingRestockingInfoMessage from './ConsumablesOnboardingRestockingInfoMessage';
import ConsumablesProductQuantityCard from '../ConsumablesProductQuantityCard';
import CostSummaryBox from '../CostSummaryBox';
import {
  createInitialOnboardingOrder,
  getConsumablesProductDeliveryFees,
  getConsumablesProductSubscriptions,
  setUpProductSubscriptions
} from '../requests';
import { calculateDeliveryFee, nextDeliveryDate } from '../utils';

const ConsumablesStartSubscriptionPage = () => {
  const { prevPathname } = useAppSelector(selectUIState);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { isMobile } = useContext(media);
  const tenancyId = useAppSelector(selectTenancyId);
  const servicesPaymentMethod = useAppSelector(selectServicesPaymentMethod);
  const [showUpdatePaymentMethodModal, setShowUpdatePaymentMethodModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [paymentMethodLoaded, setPaymentMethodLoaded] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const productSubscriptions = useAppSelector(selectProductSubscriptions);
  const containerRef = useRef<HTMLDivElement>(null);
  const deliveryFees = useAppSelector(selectDeliveryFees);
  const prevPathnamePremium = prevPathname?.endsWith('premium');
  const prevPathnameBasic = prevPathname?.includes('basic');
  const backButtonHref =
    (prevPathnamePremium || prevPathnameBasic) && prevPathname ? prevPathname : PANTRY_SUBSCRIPTION_PATH;
  const itemsSubtotal = productSubscriptions?.reduce((acc, curr) => {
    return acc + curr.consumables_product.amount_cents * curr.order_quantity;
  }, 0);
  const salesTaxRate = useAppSelector(selectSalesTaxRate);
  const salesTaxAmountCents = itemsSubtotal ? (salesTaxRate ? Math.round((itemsSubtotal * salesTaxRate) / 100) : 0) : 0;

  const loadPaymentMethods = useCallback(() => {
    if (!tenancyId) return;
    listPaymentMethods(tenancyId).then(({ data }) => {
      dispatch(tenancyActions.setPaymentMethods(data));

      const servicesPaymentMethod = data.find(paymentMethod => paymentMethod.metadata.services_payment_method);
      const rentPaymentMethod = data.find(paymentMethod => paymentMethod.metadata.rent_payment_method);
      dispatch(tenancyActions.setServicesPaymentMethod(servicesPaymentMethod));
      dispatch(tenancyActions.setRentPaymentMethod(rentPaymentMethod));
      setPaymentMethodLoaded(true);
    });
  }, [tenancyId, dispatch]);

  useEffect(() => {
    loadPaymentMethods();
    if (!productSubscriptions) {
      getConsumablesProductSubscriptions().then(({ data }) => {
        dispatch(actions.setConsumablesProductSubscriptions(data));
      });
    }
    if (!deliveryFees) {
      getConsumablesProductDeliveryFees().then(({ data }) => {
        dispatch(actions.setDeliveryFees(data));
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuantityChange = async (productSubscription: ConsumablesProductSubscription, quantity: number) => {
    try {
      const response = await setUpProductSubscriptions([
        {
          consumables_product_id: productSubscription.consumables_product.id,
          quantity
        }
      ]);
      if (response.status === 200) {
        dispatch(actions.setConsumablesProductSubscriptions(response.data.consumables_product_subscriptions));
      } else {
        showSnackbar({
          message: 'Failed to increase quantity. Please refresh and try again.',
          negative: true
        });
      }
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'Failed to increase quantity. Please refresh and try again.',
        negative: true
      });
    }
  };

  const deliveryDate = nextDeliveryDate();

  if (!paymentMethodLoaded) return <LoadingScreen />;

  if (!productSubscriptions || productSubscriptions.length === 0) {
    return <Redirect to={PANTRY_SUBSCRIPTION_PATH} />;
  }

  const deliveryFee = calculateDeliveryFee(productSubscriptions ?? [], deliveryFees);

  const handleStartService = async () => {
    if (!productSubscriptions || productSubscriptions.length === 0) {
      showSnackbar({
        message: 'Please add items to your order',
        negative: true
      });
      return;
    }
    if (!servicesPaymentMethod) {
      showSnackbar({
        message: 'Please add a payment method',
        negative: true
      });
      return;
    }
    try {
      const createInitialOnboardingOrderResponse = await createInitialOnboardingOrder();
      if (createInitialOnboardingOrderResponse.status !== 200) {
        showSnackbar({
          message: 'Failed to start service. Please try again.',
          negative: true
        });
        return;
      } else {
        dispatch(
          actions.setConsumablesProductSubscriptions(
            createInitialOnboardingOrderResponse.data.consumables_product_subscriptions
          )
        );
        dispatch(actions.setConsumablesOrders(createInitialOnboardingOrderResponse.data.consumables_orders));
        history.push(PANTRY_SUBSCRIPTION_PATH);
      }
    } catch (err) {
      showSnackbar({
        message: 'An error occurred. Refresh the page and try again.',
        negative: true
      });
    }
  };

  const totalCents = (itemsSubtotal ?? 0) + (deliveryFee || 0) + (salesTaxAmountCents || 0);

  const legalText = (
    <Layout>
      <Text size="tag" color="gray-400">
        Your payment method will be charged the specified amount at the indicated frequency. You authorize charges for
        future orders. Prices are subject to change as per Codi's{' '}
        <Link href="/terms">
          <Text color="gray-400" size="tag" underline inline __style={{ textUnderlineOffset: '2px' }}>
            Terms of Service
          </Text>
        </Link>
        . You may cancel at any time, subject to the Minimum Rental Term outlined in Codi's{' '}
        <Link href="/terms#managed-services">
          <Text color="gray-400" size="tag" underline inline __style={{ textUnderlineOffset: '2px' }}>
            Managed Services Terms
          </Text>
        </Link>
        . By clicking 'Start service', you acknowledge that you have read, understood, and agreed to Codi's{' '}
        <Link href="/terms#managed-services">
          <Text color="gray-400" size="tag" underline inline __style={{ textUnderlineOffset: '2px' }}>
            Managed Services Terms
          </Text>
        </Link>
        , which are incorporated into Codi's{' '}
        <Link href="/terms">
          <Text color="gray-400" size="tag" underline inline __style={{ textUnderlineOffset: '2px' }}>
            Terms of Service
          </Text>
        </Link>
        .
      </Text>
    </Layout>
  );

  return (
    <Layout flex direction="column" width="100%" height="100%" {...(isMobile ? { position: 'relative' } : {})}>
      <StickyContainer zIndex={HEADER_Z_INDEX}>
        <Layout
          width="100%"
          flex
          align="center"
          {...(isMobile
            ? { height: 68, paddingRight: 60, paddingLeft: 12 }
            : { paddingX: 40, height: 88, paddingY: 24 })}
          background="white"
          borderBottom
        >
          <IconButton
            size="lg"
            type="noBackground"
            name={isMobile ? 'leftChevron' : 'leftArrow'}
            href={backButtonHref}
          />
          {isMobile && (
            <Layout width="100%" justify="center" align="center">
              <Text size="headline-sm">Start restocking service</Text>
            </Layout>
          )}
        </Layout>
      </StickyContainer>
      {isMobile && (
        <Layout color="blue-100" paddingX={24} paddingY={20} gap={12} direction="column">
          <Text size="headline-xs" color="purple-gradient">
            Your items — restocked like magic
          </Text>
          <Text size="body-xs">
            We don’t just deliver — we restock and organize too. All items are unpacked and placed in your kitchen
            cabinets, fridge, and restrooms.
          </Text>
        </Layout>
      )}
      {isMobile && <Layout minHeight={8} color="gray-25" position="relative" width="100%" />}
      <Layout
        {...(isMobile
          ? { paddingX: APP_PAGE_PADDING_X_MOBILE, paddingTop: 24 }
          : {
              paddingX: 40,
              paddingTop: 40,
              paddingBottom: 64,
              flex: true,
              direction: 'row',
              gap: 80,
              justify: 'center'
            })}
      >
        <Layout
          flex
          direction="column"
          {...(isMobile ? {} : { width: 724, gap: 36 })}
          __ref={containerRef}
          gap={isMobile ? 24 : 48}
        >
          {!isMobile && (
            <>
              <Text size="headline-lg">Start restocking service</Text>
              <ConsumablesOnboardingRestockingBanner size={'sm'} />
            </>
          )}

          <Layout flex direction="column" gap={24}>
            <Text size="headline-sm">Billing</Text>
            <PaymentMethodCard
              onClick={() => setShowUpdatePaymentMethodModal(true)}
              onUpdate={() => setShowUpdatePaymentMethodModal(true)}
            />
          </Layout>
          {isMobile && (
            <Layout minHeight={8} color="gray-25" position="relative" left={-24} width="calc(100% + 48px)" />
          )}
          <Layout direction="column" gap={36}>
            <Layout flex>
              <Text size={'headline-sm'} semibold>
                Items
              </Text>
            </Layout>
            <Layout
              flex
              direction="column"
              gap={36}
              paddingBottom={isMobile ? 24 : 36}
              borderBottom={isMobile ? false : true}
            >
              {productSubscriptions?.map(productSubscription => (
                <ConsumablesProductQuantityCard
                  isSaved={productSubscription.id !== undefined}
                  key={productSubscription.consumables_product.id}
                  product={productSubscription.consumables_product}
                  quantity={productSubscription.order_quantity}
                  onQuantityChange={quantity => {
                    handleQuantityChange(productSubscription, quantity);
                  }}
                />
              ))}
            </Layout>
            {isMobile && (
              <Layout minHeight={8} color="gray-25" position="relative" left={-24} width="calc(100% + 48px)" />
            )}
            {!isMobile && (
              <Layout direction="column" gap={24}>
                <Layout direction="row" gap={24} align="center">
                  <Layout width={272}>
                    <PillButton
                      type="purpleGradient"
                      size="lg"
                      text="Start service"
                      onClick={handleStartService}
                      fullWidth
                    />
                  </Layout>
                  <Text size="headline-xs">Total: {totalCents ? formatUsd(totalCents / 100) : 0}</Text>
                </Layout>
                {legalText}
              </Layout>
            )}
          </Layout>
        </Layout>
        {!isMobile && (
          <Layout width={414}>
            <CostSummaryBox
              paddingX={36}
              totalSubtext={
                <Text size="body-xs" color="gray-400">
                  First charge on {formatDate({ date: deliveryDate, format: 'MED_WITH_SHORT_WEEKDAY' })}
                </Text>
              }
              paddingY={36}
              itemsSubtotalCents={itemsSubtotal ?? 0}
              disclaimerMessage={<ConsumablesOnboardingRestockingInfoMessage />}
              deliveryFeeCents={deliveryFee}
              backgroundColor="gray-25"
              footer={
                <Layout
                  direction="column"
                  align="center"
                  paddingTop={16}
                  paddingBottom={36}
                  marginTop={8}
                  paddingX={36}
                  gap={16}
                  borderBottomLeftRadius={16}
                  borderBottomRightRadius={16}
                >
                  <PillButton
                    fullWidth
                    borderRadius={40}
                    type="purpleGradient"
                    size="lg"
                    text="Start service"
                    onClick={handleStartService}
                  />
                  {legalText}
                </Layout>
              }
            />
          </Layout>
        )}
      </Layout>
      {isMobile && (
        <Layout width="100%">
          <CostSummaryBox
            paddingX={24}
            totalSubtext={
              <Text size="body-xs" color="gray-400">
                First charge on {formatDate({ date: deliveryDate, format: 'MED_WITH_SHORT_WEEKDAY' })}
              </Text>
            }
            paddingY={24}
            itemsSubtotalCents={itemsSubtotal ?? 0}
            disclaimerMessage={<ConsumablesOnboardingRestockingInfoMessage />}
            deliveryFeeCents={deliveryFee}
            backgroundColor="white"
            footer={
              <Layout
                direction="column"
                align="center"
                paddingTop={16}
                paddingBottom={36}
                marginTop={8}
                paddingX={24}
                gap={16}
                borderBottomLeftRadius={16}
                borderBottomRightRadius={16}
              >
                <PillButton
                  fullWidth
                  borderRadius={40}
                  type="purpleGradient"
                  size="lg"
                  text="Start service"
                  onClick={handleStartService}
                />
                {legalText}
              </Layout>
            }
          />
        </Layout>
      )}
      <StripePaymentMethodModal
        isVisible={showPaymentMethodModal}
        onUpdate={loadPaymentMethods}
        onClose={() => {
          setShowPaymentMethodModal(false);
        }}
        onAddNew={paymentMethod => {
          setSelectedPaymentMethod(paymentMethod);
        }}
      />
      <UpdateStripePaymentMethodModal
        showManagePaymentMethods
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        isVisible={showUpdatePaymentMethodModal}
        type="services"
        onClose={() => {
          if (showPaymentMethodModal) return;
          setShowUpdatePaymentMethodModal(false);
          setTimeout(() => {
            setSelectedPaymentMethod(null);
          }, ANIMATION_DURATION);
        }}
        onSelectAddNewPaymentMethod={() => {
          setShowPaymentMethodModal(true);
        }}
      />
    </Layout>
  );
};

export default ConsumablesStartSubscriptionPage;
